/* ==========================================================================
   Lumen — facets.js
   AJAX collection filtering + sorting via the Section Rendering API.

   <facet-collection data-section-id data-collection-url> wires:
     [data-facet-form]           facet form(s): sidebar + drawer (synced by id)
     [data-sort-by]              sort <select> (reads data-default-sort)
     [data-sort-form]            toolbar sort form (noscript fallback)
     [data-collection-results]   pills + grid + pagination (innerHTML swapped)
     [data-product-count]        result count text (textContent swapped)
     [data-active-count]         active-filter badge (text + hidden swapped)
     [data-price-input]          price min/max inputs (debounced 500ms)
     [data-facet-remove]         pill / clear-all links (intercepted)
     [data-view-btn="grid|list"] view toggles; [data-collection-grid] gets
                                 data-view, choice saved to localStorage

   Progressive enhancement: without JS the facet forms submit as normal GET
   requests (noscript Apply buttons) and pills/pagination are plain links.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('facet-collection')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var VIEW_KEY = 'lumen:collection-view';

  function debounce(fn, ms) {
    if (window.theme && theme.utils && theme.utils.debounce) return theme.utils.debounce(fn, ms);
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  customElements.define('facet-collection', class extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.sectionId = this.getAttribute('data-section-id');
      this._priceDebounced = debounce(function (form) { self.applyFilters(form); }, 500);

      this._onPopState = function () {
        self.fetchAndRender(window.location.pathname, new URLSearchParams(window.location.search), false, false);
      };
      window.addEventListener('popstate', this._onPopState);

      /* Checkbox / boolean facets + sort select */
      this.addEventListener('change', function (e) {
        if (e.target.closest('[data-sort-by]')) {
          self.applyFilters(self.querySelector('[data-facet-form]'));
          return;
        }
        if (e.target.hasAttribute('data-price-input')) return; /* debounced via input */
        var form = e.target.closest('[data-facet-form]');
        if (form) self.applyFilters(form);
      });

      /* Price inputs — debounce 500ms */
      this.addEventListener('input', function (e) {
        if (!e.target.hasAttribute('data-price-input')) return;
        var form = e.target.closest('[data-facet-form]');
        if (form) self._priceDebounced(form);
      });

      /* Enter key in a field / noscript-style submits */
      this.addEventListener('submit', function (e) {
        var form = e.target;
        if (form.hasAttribute('data-facet-form')) {
          e.preventDefault();
          self.applyFilters(form);
        } else if (form.hasAttribute('data-sort-form')) {
          e.preventDefault();
          self.applyFilters(self.querySelector('[data-facet-form]'));
        }
      });

      /* View toggles, active-filter pills, pagination links */
      this.addEventListener('click', function (e) {
        var viewBtn = e.target.closest('[data-view-btn]');
        if (viewBtn && self.contains(viewBtn)) {
          self.applyView(viewBtn.getAttribute('data-view-btn'), true);
          return;
        }

        var link = e.target.closest('[data-facet-remove], [data-collection-results] .pagination a');
        if (!link || !self.contains(link)) return;
        var url;
        try { url = new URL(link.href, window.location.origin); } catch (err) { return; }
        if (url.origin !== window.location.origin) return;
        e.preventDefault();
        var params = url.searchParams;
        params.delete('section_id');
        self.fetchAndRender(url.pathname, params, true, !!link.closest('.pagination'));
      });

      /* Restore saved grid/list view */
      var saved = 'grid';
      try { saved = window.localStorage.getItem(VIEW_KEY) || 'grid'; } catch (err) { /* storage unavailable */ }
      this.applyView(saved === 'list' ? 'list' : 'grid', false);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this._onPopState);
      if (this._abort) this._abort.abort();
    }

    /* Build a query string from a facet form plus the sort select, then render. */
    applyFilters(form) {
      var params = new URLSearchParams();
      if (form) {
        var fd = new FormData(form);
        fd.forEach(function (value, key) {
          if (value !== '' && key !== 'sort_by') params.append(key, value);
        });
      } else {
        /* Sort-only mode (no facet form rendered): keep current query params */
        params = new URLSearchParams(window.location.search);
        params.delete('sort_by');
        params.delete('page');
        params.delete('section_id');
      }
      var sort = this.querySelector('[data-sort-by]');
      if (sort && sort.value && sort.value !== sort.getAttribute('data-default-sort')) {
        params.set('sort_by', sort.value);
      }
      this.fetchAndRender(window.location.pathname, params, true, false);
    }

    fetchAndRender(path, params, pushHistory, scrollToResults) {
      var self = this;
      if (this._abort) this._abort.abort();
      var controller = new AbortController();
      this._abort = controller;

      this.setAttribute('data-loading', '');
      var results = this.querySelector('[data-collection-results]');
      if (results) results.setAttribute('aria-busy', 'true');

      var query = params.toString();
      var cleanUrl = query ? path + '?' + query : path;
      var fetchUrl = path + '?section_id=' + encodeURIComponent(this.sectionId) + (query ? '&' + query : '');

      fetch(fetchUrl, { signal: controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Facets fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          self.renderFrom(doc);
          if (pushHistory) window.history.pushState({ lumenFacets: true }, '', cleanUrl);
          if (scrollToResults) self.scrollToResults();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          window.location.assign(cleanUrl); /* graceful fallback: full navigation */
        })
        .then(function () {
          if (self._abort !== controller) return; /* superseded by a newer request */
          self.removeAttribute('data-loading');
          var r = self.querySelector('[data-collection-results]');
          if (r) r.setAttribute('aria-busy', 'false');
        });
    }

    renderFrom(doc) {
      /* Form swaps destroy the focused node — remember it (price inputs
         especially: the shopper may still be typing when the fetch lands) */
      var active = document.activeElement;
      var activeId = active && active.id && this.contains(active) ? active.id : null;
      var activeValue = activeId && active.hasAttribute('data-price-input') ? active.value : null;

      /* Results: pills + grid + pagination */
      var curResults = this.querySelector('[data-collection-results]');
      var newResults = doc.querySelector('[data-collection-results]');
      if (curResults && newResults) curResults.innerHTML = newResults.innerHTML;

      /* Facet forms (desktop sidebar + mobile drawer), matched by id */
      doc.querySelectorAll('[data-facet-form]').forEach(function (newForm) {
        var current = newForm.id ? document.getElementById(newForm.id) : null;
        if (current) current.innerHTML = newForm.innerHTML;
      });

      /* Restore focus (and in-progress typing) lost to the innerHTML swaps */
      if (activeId && document.activeElement !== active) {
        var refocus = document.getElementById(activeId);
        if (refocus) {
          if (activeValue !== null && refocus.hasAttribute('data-price-input')) {
            refocus.value = activeValue;
          }
          refocus.focus();
        }
      }

      /* Sort select — keep in sync after back/forward navigation */
      var newSort = doc.querySelector('[data-sort-by]');
      if (newSort) {
        this.querySelectorAll('[data-sort-by]').forEach(function (el) { el.value = newSort.value; });
      }

      /* Result count + active-filter badge */
      var newCount = doc.querySelector('[data-product-count]');
      if (newCount) {
        var text = newCount.textContent.trim();
        this.querySelectorAll('[data-product-count]').forEach(function (el) { el.textContent = text; });
      }
      var newBadge = doc.querySelector('[data-active-count]');
      if (newBadge) {
        this.querySelectorAll('[data-active-count]').forEach(function (el) {
          el.textContent = newBadge.textContent;
          el.hidden = newBadge.hidden;
        });
      }

      /* Links rendered through the Section Rendering API can carry section_id */
      this.querySelectorAll('a[href*="section_id="]').forEach(function (a) {
        try {
          var u = new URL(a.getAttribute('href'), window.location.origin);
          u.searchParams.delete('section_id');
          var q = u.searchParams.toString();
          a.setAttribute('href', u.pathname + (q ? '?' + q : '') + u.hash);
        } catch (err) { /* leave as-is */ }
      });

      /* Swapped-in nodes were never observed by the core reveal observer */
      this.querySelectorAll('[data-collection-results] .reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });

      /* Re-apply saved grid/list view to the fresh grid */
      this.applyView(this.currentView || 'grid', false);
    }

    applyView(view, persist) {
      view = view === 'list' ? 'list' : 'grid';
      this.currentView = view;
      var grid = this.querySelector('[data-collection-grid]');
      if (grid) grid.setAttribute('data-view', view);
      this.querySelectorAll('[data-view-btn]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(btn.getAttribute('data-view-btn') === view));
      });
      if (persist) {
        try { window.localStorage.setItem(VIEW_KEY, view); } catch (err) { /* storage unavailable */ }
      }
    }

    scrollToResults() {
      var results = this.querySelector('[data-collection-results]');
      if (!results) return;
      /* Land the results just below the (possibly sticky) header */
      var headerBottom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-bottom'));
      if (isNaN(headerBottom)) headerBottom = 73;
      var top = results.getBoundingClientRect().top + window.scrollY - headerBottom - 24;
      window.scrollTo({ top: Math.max(top, 0), behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  });
})();
